<template>
  <header class="sticky top-0 z-30 bg-gray-50/95 backdrop-blur">
    <div class="nav-transition mx-auto max-w-10xl px-4 lg:px-8">
      <div class="grid items-start gap-4 py-4 md:grid-cols-[auto_1fr_auto]">
        <div class="flex items-center gap-2 self-start">
          <RouterLink to="/" class="text-lg font-semibold text-ink"><img src="@/assets/logo.png" alt="Invi8 Logo" style="height:40px"></RouterLink>  
        </div>

        <div class="flex flex-col items-center justify-center gap-2">
          <form
            v-if="showSearch"
            class="flex w-full max-w-4xl flex-col gap-3 md:flex-row md:items-center"
            @submit.prevent="submitNavbarSearch"
          >
            <div class="relative flex w-full overflow-visible rounded-[28px] border border-gray-200 bg-white shadow-sm md:min-h-[56px]">
              <label class="sr-only" for="navbar-location">Location</label>
              <div class="relative flex-1">
                <i class="ri-map-pin-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-lg text-muted"></i>
                <input
                  ref="locationInputRef"
                  id="navbar-location"
                  v-model.trim="locationSearchQuery"
                  type="search"
                  placeholder="Location"
                  class="w-full rounded-l-[28px] border-0 bg-transparent px-12 py-3 text-sm text-ink transition focus:outline-none md:h-full"
                  @focus="openLocationPicker"
                  @input="searchNavbarLocation"
                >
                <div
                  v-if="locationSearchOpen"
                  ref="locationDropdownRef"
                  class="absolute left-0 right-0 top-full z-40 mt-3 overflow-hidden rounded-[22px] border border-[#eadfd4] bg-white shadow-[0_18px_40px_rgba(0,0,0,0.12)]"
                >
                  <div class="border-b border-[#f0e5da] px-4 py-3">
                    <p class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">Search location</p>
                    <p class="mt-1 text-xs text-muted">Type an address, city, or area. You can also use your current location.</p>
                  </div>

                  <div class="max-h-[320px] overflow-y-auto p-3">
                    <button
                      type="button"
                      class="flex w-full items-center gap-3 rounded-2xl border border-[#eadfd4] px-4 py-3 text-left transition hover:border-brand hover:bg-[#fbf1e6]"
                      :disabled="locationCurrentLoading"
                      @click="useNavbarCurrentLocation"
                    >
                      <span class="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#fff2ea] text-brand">
                        <i class="ri-navigation-line text-lg"></i>
                      </span>
                      <span class="min-w-0 flex-1">
                        <span class="block text-sm font-semibold text-ink">{{ locationCurrentLoading ? 'Detecting...' : 'Use current location' }}</span>
                        <span class="mt-1 block text-xs text-muted">Auto-fill from your device location</span>
                      </span>
                    </button>

                    <div class="mt-3 space-y-2">
                      <button
                        v-for="(item, index) in locationSearchResults"
                        :key="`${item.placeId || item.label}-${index}`"
                        type="button"
                        class="flex w-full items-start gap-3 rounded-2xl px-4 py-3 text-left transition hover:bg-[#fcf7f2]"
                        @click="selectNavbarLocation(item)"
                      >
                        <span class="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#f1f6ff] text-[#2d62b3]">
                          <i class="ri-map-pin-2-line text-lg"></i>
                        </span>
                        <span class="min-w-0 flex-1">
                          <span class="block text-sm font-semibold text-ink">{{ item.label }}</span>
                          <span class="mt-1 block text-xs text-muted">{{ item.meta }}</span>
                        </span>
                      </button>
                    </div>

                    <p v-if="locationSearchLoading" class="mt-3 rounded-2xl border border-dashed border-[#eadfd4] px-4 py-4 text-sm text-muted">
                      Searching locations...
                    </p>
                    <p v-else-if="locationPickerError" class="mt-3 rounded-2xl border border-dashed border-red-200 bg-red-50 px-4 py-4 text-sm text-red-600">
                      {{ locationPickerError }}
                    </p>
                    <p v-else-if="!locationSearchResults.length" class="mt-3 rounded-2xl border border-dashed border-[#eadfd4] px-4 py-4 text-sm text-muted">
                      Enter at least 3 characters to search for a location.
                    </p>
                  </div>
                </div>
              </div>

              <div class="my-2 hidden w-px bg-gray-200 md:block"></div>

              <label class="sr-only" for="navbar-search-type">Search type</label>
              <div class="relative min-w-[130px] md:w-[150px]">
                <i class="ri-list-unordered pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-lg text-muted"></i>
                <select
                  id="navbar-search-type"
                  v-model="searchType"
                  class="h-full w-full appearance-none border-0 bg-transparent px-12 py-3 pr-10 text-sm font-medium text-ink transition focus:outline-none"
                >
                  <option value="venues">Venue</option>
                  <option value="vendors">Vendor</option>
                </select>
                <i class="ri-arrow-down-s-line pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-base text-muted"></i>
              </div>

              <div class="my-2 hidden w-px bg-gray-200 md:block"></div>

              <label class="sr-only" for="navbar-search-key">Search</label>
              <div class="relative flex-[1.3]">
                <i class="ri-search-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-lg text-muted"></i>
                <input
                  id="navbar-search-key"
                  v-model.trim="searchKey"
                  type="search"
                  :placeholder="searchType === 'venues' ? 'Search venues, halls, styles...' : 'Search vendors, services, styles...'"
                  class="w-full border-0 bg-transparent px-12 py-3 text-sm text-ink transition focus:outline-none md:h-full"
                >
              </div>

              <button
                type="submit"
                class="inline-flex items-center justify-center rounded-[24px] bg-brand px-5 py-3 text-sm font-semibold text-white transition hover:bg-brandDark md:m-1"
              >
                Search
              </button>
            </div>
          </form>
        </div>

        <div class="flex items-center gap-3 justify-end self-start">
          <RouterLink v-if="!hideDashboardExtras" class="hidden text-sm font-semibold text-ink md:inline" to="/vendor-signup">Become a Vendor</RouterLink> 
          <div v-if="isLoggedIn" class="relative flex items-center">
            <button
              ref="profileButtonRef"
              type="button"
              class="flex items-center gap-2 rounded-full border border-gray-200 bg-white px-2.5 py-1.5 text-sm font-semibold text-ink shadow-sm transition hover:border-gray-300"
              @click="profileMenuOpen = !profileMenuOpen"
            >
              <span class="flex h-8 w-8 items-center justify-center rounded-full bg-[#FFE5EC] text-brand">
                <i class="ri-user-3-line text-base"></i>
              </span>
              <span class="hidden lg:inline">{{ displayName }}</span>
              <i class="ri-arrow-down-s-line text-base text-muted"></i>
            </button>
            <transition name="fade">
              <div
                v-if="profileMenuOpen"
                ref="profileDropdownRef"
                class="absolute right-0 top-full mt-3 w-56 rounded-2xl border border-gray-100 bg-white p-3 shadow-[0_18px_40px_rgba(0,0,0,0.12)] z-40"
              >
                <div class="px-2 pb-2 text-xs font-semibold uppercase tracking-wide text-muted">Account</div>
                <RouterLink class="flex items-center justify-between rounded-xl px-3 py-2 text-sm font-semibold text-ink transition hover:bg-gray-50" to="/dashboard">
                  <span>Dashboard</span>
                  <i class="ri-dashboard-line text-sm text-muted"></i>
                </RouterLink>
                <RouterLink class="flex items-center justify-between rounded-xl px-3 py-2 text-sm font-semibold text-ink transition hover:bg-gray-50" to="/dashboard/settings">
                  <span>Settings</span>
                  <i class="ri-settings-3-line text-sm text-muted"></i>
                </RouterLink>
                <button
                  type="button"
                  class="mt-1 flex w-full items-center justify-between rounded-xl px-3 py-2 text-sm font-semibold text-ink transition hover:bg-gray-50"
                  @click="handleLogout"
                >
                  <span>Logout</span>
                  <i class="ri-logout-box-r-line text-sm text-muted"></i>
                </button>
              </div>
            </transition>
          </div>
          <RouterLink
            v-else
            class="flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white text-ink shadow-sm transition hover:border-gray-300"
            to="/login"
            aria-label="Log in"
          >
            <i class="ri-user-line text-lg"></i>
          </RouterLink>
          <button v-if="!hideDashboardExtras" class="flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white text-ink shadow-sm transition hover:border-gray-300 lg:hidden" aria-label="Open menu" @click="ui.toggleSidenav">
            <svg class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <div v-if="!hideDashboardExtras" class="relative hidden lg:block">
            <button ref="menuButtonRef" class="flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white text-ink shadow-sm transition hover:border-gray-300" aria-label="Open menu" @click="desktopMenuOpen = !desktopMenuOpen">
              <svg class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <transition name="fade">
              <div
                v-if="desktopMenuOpen"
                ref="dropdownRef"
                class="absolute right-0 top-full mt-3 w-72 rounded-2xl border border-gray-100 bg-white p-4 shadow-[0_18px_40px_rgba(0,0,0,0.12)] z-40"
              >
                <div class="mb-2 flex items-center gap-2 text-sm font-semibold text-ink">
                  <span class="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-lg">?</span>
                  <span>Navigate</span>
                </div>
                <div class="grid gap-2 text-sm text-ink">
                  <RouterLink
                    v-for="link in navLinks"
                    :key="link.to"
                    class="flex items-center justify-between rounded-xl px-3 py-2 transition hover:bg-gray-50"
                    :to="link.to"
                  >
                    <span class="font-semibold">{{ link.label }}</span>
                    <span class="text-xs text-muted">→</span>
                  </RouterLink>
                </div>
                <div class="mt-3 flex gap-2">
                  <RouterLink
                    v-if="!isLoggedIn"
                    class="flex-1 rounded-full border border-gray-200 px-3 py-2 text-center text-xs font-semibold text-ink transition hover:border-brand hover:text-brand"
                    to="/login"
                  >
                    Log in
                  </RouterLink>
                  <RouterLink
                    v-if="!isLoggedIn"
                    class="flex-1 rounded-full bg-brand px-3 py-2 text-center text-xs font-semibold text-white transition hover:bg-brandDark"
                    to="/register"
                  >
                    Register
                  </RouterLink>
                  <RouterLink
                    v-if="isLoggedIn"
                    class="flex-1 rounded-full border border-gray-200 px-3 py-2 text-center text-xs font-semibold text-ink transition hover:border-brand hover:text-brand"
                    to="/dashboard/settings"
                  >
                    Settings
                  </RouterLink>
                  <button
                    v-if="isLoggedIn"
                    type="button"
                    class="flex-1 rounded-full bg-brand px-3 py-2 text-center text-xs font-semibold text-white transition hover:bg-brandDark"
                    @click="handleLogout"
                  >
                    Logout
                  </button>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </div>
  </header>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { useUiStore } from '@/stores/ui'
import { clearAuth, getAuthUser, isAuthenticated } from '@/services/auth'

const GOOGLE_API_KEY = 'AIzaSyDOAEexenU_AutS0ULOGzNa3HQZHpikt4A'

const ui = useUiStore()
const route = useRoute()
const router = useRouter()

const desktopMenuOpen = ref(false)
const dropdownRef = ref(null)
const menuButtonRef = ref(null)
const profileMenuOpen = ref(false)
const profileDropdownRef = ref(null)
const profileButtonRef = ref(null)
const searchLocation = ref(String(route.query.location || ''))
const searchKey = ref(String(route.query.search_key || ''))
const locationSearchQuery = ref(String(route.query.location || ''))
const locationSearchResults = ref([])
const locationSearchLoading = ref(false)
const locationCurrentLoading = ref(false)
const locationPickerError = ref('')
const locationSearchOpen = ref(false)
const locationInputRef = ref(null)
const locationDropdownRef = ref(null)
const searchLat = ref(String(route.query.lat || ''))
const searchLng = ref(String(route.query.lng || ''))
const searchType = ref('venues')
const googleReady = ref(false)
const googleScriptLoading = ref(false)
const autocompleteService = ref(null)
const placesService = ref(null)
const geocoder = ref(null)

const navLinks = [
  { to: '/', label: 'Home' },
  { to: '/vendors', label: 'Vendors' },
  { to: '/venues', label: 'Venues' }, 
  { to: '/pricing', label: 'Pricing' },
  { to: '/faqs', label: 'FAQs' },
  { to: '/categories', label: 'Categories' },
  { to: '/use-cases', label: 'Use Cases' },
  { to: '/blog', label: 'Blog' },
  { to: '/about', label: 'About' },
  { to: '/contact', label: 'Contact' },
  { to: '/privacy-policy', label: 'Privacy' },
  { to: '/terms', label: 'Terms' }
]
 
const isVendorDetail = computed(() => (router.currentRoute.value.path || '').startsWith('/vendor/'))
const currentPath = computed(() => router.currentRoute.value.path || '')
const currentName = computed(() => router.currentRoute.value.name || '')
const isDashboardRoute = computed(() => currentPath.value.toLowerCase().startsWith('/dashboard'))

const hiddenPaths = ['/vendor-signup', '/vendor-onboard', '/login', '/register', '/forgot-password', '/dashboard', '/404', '/not-found']
const hiddenNames = ['VendorSignup', 'VendorOnboard', 'Login', 'Register', 'ForgotPassword', 'Dashboard', 'DashboardVenues', 'DashboardPackages', 'DashboardSettings', 'NotFound']

const hideNavUi = computed(() => {
  const path = currentPath.value.toLowerCase()
  if (isVendorDetail.value) return true
  if (hiddenPaths.some((p) => path.startsWith(p))) return true
  const name = (currentName.value || '').toString().toLowerCase()
  return hiddenNames.map((n) => n.toLowerCase()).includes(name)
})

const showSearch = computed(() => !hideNavUi.value)
const hideDashboardExtras = computed(() => isLoggedIn.value && isDashboardRoute.value)
const navCollapsed = ref(false)
const forceExpanded = ref(false)
const forceExpandedAt = ref(0)

const handleClickOutside = (event) => {
  const target = event.target
  if (locationSearchOpen.value) {
    if (locationDropdownRef.value && locationDropdownRef.value.contains(target)) return
    if (locationInputRef.value && locationInputRef.value.contains(target)) return
    locationSearchOpen.value = false
  }
  if (desktopMenuOpen.value) {
    if (dropdownRef.value && dropdownRef.value.contains(target)) return
    if (menuButtonRef.value && menuButtonRef.value.contains(target)) return
    desktopMenuOpen.value = false
  }
  if (profileMenuOpen.value) {
    if (profileDropdownRef.value && profileDropdownRef.value.contains(target)) return
    if (profileButtonRef.value && profileButtonRef.value.contains(target)) return
    profileMenuOpen.value = false
  }
}

const handleEsc = (event) => {
  if (event.key === 'Escape') {
    locationSearchOpen.value = false
    desktopMenuOpen.value = false
    profileMenuOpen.value = false
  }
}

const collapseThreshold = 80
const handleScroll = () => {
  const y = window.scrollY || document.documentElement.scrollTop
  const nearTop = y < 12

  if (nearTop) {
    navCollapsed.value = false
    forceExpanded.value = false
    return
  }

  if (forceExpanded.value) {
    const moved = Math.abs(y - forceExpandedAt.value) > 50
    if (moved && y > forceExpandedAt.value) {
      forceExpanded.value = false
    } else {
      navCollapsed.value = false
      return
    }
  }

  navCollapsed.value = y > collapseThreshold
}

const getSearchTypeFromRoute = () => {
  const queryType = String(route.query.type || '').toLowerCase()
  if (queryType === 'vendors' || queryType === 'vendor') return 'vendors'
  if (queryType === 'venues' || queryType === 'venue') return 'venues'

  const path = currentPath.value.toLowerCase()
  if (path.startsWith('/vendors')) return 'vendors'
  if (path.startsWith('/venues')) return 'venues'
  return 'venues'
}

const initGooglePlacesServices = () => {
  if (typeof window === 'undefined' || !window.google?.maps?.places) return false
  googleReady.value = true
  autocompleteService.value = autocompleteService.value || new window.google.maps.places.AutocompleteService()
  placesService.value = placesService.value || new window.google.maps.places.PlacesService(document.createElement('div'))
  geocoder.value = geocoder.value || new window.google.maps.Geocoder()
  googleScriptLoading.value = false
  return true
}

const ensureGooglePlaces = () => {
  if (initGooglePlacesServices()) return Promise.resolve(true)
  if (!GOOGLE_API_KEY) return Promise.resolve(false)

  return new Promise((resolve) => {
    const existingScript = document.querySelector('script[data-google-maps="places"], script[src*="maps.googleapis.com/maps/api/js"]')
    if (existingScript) {
      if (googleScriptLoading.value) {
        existingScript.addEventListener('load', () => resolve(initGooglePlacesServices()), { once: true })
        existingScript.addEventListener('error', () => resolve(false), { once: true })
        return
      }
      resolve(initGooglePlacesServices())
      return
    }

    googleScriptLoading.value = true
    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&libraries=places`
    script.async = true
    script.defer = true
    script.dataset.googleMaps = 'places'
    script.onload = () => resolve(initGooglePlacesServices())
    script.onerror = () => {
      googleScriptLoading.value = false
      resolve(false)
    }
    document.head.appendChild(script)
  })
}

const openLocationPicker = () => {
  locationSearchOpen.value = true
  locationPickerError.value = ''
  ensureGooglePlaces().then((loaded) => {
    if (!loaded && !locationSearchQuery.value.trim()) {
      locationPickerError.value = 'Google location search is unavailable right now.'
    }
  })
}

const searchNavbarLocation = async () => {
  const query = locationSearchQuery.value.trim()
  searchLocation.value = query
  searchLat.value = ''
  searchLng.value = ''
  locationSearchOpen.value = true

  if (query.length < 3) {
    locationSearchResults.value = []
    locationPickerError.value = 'Enter at least 3 characters to search.'
    return
  }

  locationPickerError.value = ''
  locationSearchLoading.value = true
  try {
    const loaded = await ensureGooglePlaces()
    if (!loaded || !autocompleteService.value) {
      throw new Error('google-places-unavailable')
    }

    locationSearchResults.value = await new Promise((resolve, reject) => {
      autocompleteService.value.getPlacePredictions(
        {
          input: query
        },
        (predictions, status) => {
          if (status !== window.google.maps.places.PlacesServiceStatus.OK || !Array.isArray(predictions)) {
            if (status === window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
              resolve([])
              return
            }
            reject(new Error(status || 'places-search-failed'))
            return
          }

          resolve(
            predictions.slice(0, 6).map((item) => ({
              placeId: item.place_id || '',
              label: item.structured_formatting?.main_text || item.description || query,
              meta: item.structured_formatting?.secondary_text || item.description || '',
              latitude: '',
              longitude: ''
            }))
          )
        }
      )
    })

    if (!locationSearchResults.value.length) {
      locationPickerError.value = 'No matching locations found.'
    }
  } catch {
    locationSearchResults.value = []
    locationPickerError.value = 'Unable to search locations right now.'
  } finally {
    locationSearchLoading.value = false
  }
}

const resolveNavbarPlaceDetails = (placeId) => {
  if (!placeId || !placesService.value || typeof window === 'undefined' || !window.google?.maps?.places) {
    return Promise.resolve(null)
  }

  return new Promise((resolve) => {
    placesService.value.getDetails(
      {
        placeId,
        fields: ['formatted_address', 'geometry.location', 'name']
      },
      (place, status) => {
        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place) {
          resolve(null)
          return
        }

        resolve({
          label: place.formatted_address || place.name || '',
          latitude: place.geometry?.location?.lat?.() ?? '',
          longitude: place.geometry?.location?.lng?.() ?? ''
        })
      }
    )
  })
}

const selectNavbarLocation = async (item) => {
  if (!item?.label) return
  const placeDetails = item.placeId ? await resolveNavbarPlaceDetails(item.placeId) : null
  const resolvedLabel = placeDetails?.label || item.label
  searchLocation.value = resolvedLabel
  locationSearchQuery.value = resolvedLabel
  searchLat.value = String(placeDetails?.latitude || item.latitude || '')
  searchLng.value = String(placeDetails?.longitude || item.longitude || '')
  locationSearchOpen.value = false
  locationPickerError.value = ''
}

const reverseGeocodeNavbarLocation = async (latitude, longitude) => {
  const loaded = await ensureGooglePlaces()
  if (!loaded || !geocoder.value) {
    throw new Error('google-geocoder-unavailable')
  }

  const result = await geocoder.value.geocode({
    location: {
      lat: Number(latitude),
      lng: Number(longitude)
    }
  })

  return String(result?.results?.[0]?.formatted_address || 'Current location').trim()
}

const useNavbarCurrentLocation = async () => {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    locationPickerError.value = 'Geolocation is not supported in this browser.'
    return
  }

  locationPickerError.value = ''
  locationCurrentLoading.value = true
  try {
    const position = await new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 10000
      })
    })
    const latitude = position.coords.latitude
    const longitude = position.coords.longitude
    const label = await reverseGeocodeNavbarLocation(latitude, longitude)
    searchLocation.value = label
    locationSearchQuery.value = label
    searchLat.value = String(latitude)
    searchLng.value = String(longitude)
    locationSearchOpen.value = false
  } catch {
    locationPickerError.value = 'Unable to detect your current location.'
  } finally {
    locationCurrentLoading.value = false
  }
}

const submitNavbarSearch = () => {
  router.push({
    path: searchType.value === 'vendors' ? '/vendors' : '/venues',
    query: {
      type: searchType.value,
      location: searchLocation.value || undefined,
      search_key: searchKey.value || undefined,
      lat: searchLat.value || undefined,
      lng: searchLng.value || undefined
    }
  })
}

const isLoggedIn = ref(isAuthenticated())
const userProfile = ref(getAuthUser())

const refreshAuthState = () => {
  isLoggedIn.value = isAuthenticated()
  userProfile.value = getAuthUser()
}

const displayName = computed(() => {
  const user = userProfile.value || {}
  return (
    user.name ||
    user.full_name ||
    user.fullName ||
    user.username ||
    user.email ||
    'Account'
  )
})

const handleLogout = () => {
  clearAuth()
  refreshAuthState()
  router.push('/login')
}

watch(
  () => route.fullPath,
  () => {
    searchType.value = getSearchTypeFromRoute()
    searchLocation.value = String(route.query.location || '')
    locationSearchQuery.value = String(route.query.location || '')
    searchKey.value = String(route.query.search_key || '')
    searchLat.value = String(route.query.lat || '')
    searchLng.value = String(route.query.lng || '')
    refreshAuthState()
  },
  { immediate: true }
)

const removeAfterEach = router.afterEach(() => {
  desktopMenuOpen.value = false
  profileMenuOpen.value = false
})

onMounted(() => {
  window.addEventListener('click', handleClickOutside)
  window.addEventListener('keydown', handleEsc)
  window.addEventListener('scroll', handleScroll, { passive: true })
  handleScroll()
  refreshAuthState()
})

onUnmounted(() => {
  window.removeEventListener('click', handleClickOutside)
  window.removeEventListener('keydown', handleEsc)
  window.removeEventListener('scroll', handleScroll)
  if (typeof removeAfterEach === 'function') removeAfterEach()
})
</script>

<style scoped>


.nav-transition {
  transition: transform 260ms cubic-bezier(0.22, 1, 0.36, 1), opacity 220ms ease;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@keyframes bounceSlow {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-2px); }
}

.animate-bounce-slow {
  animation: bounceSlow 1.2s infinite;
}

.nav-transition a.router-link-active {
  color: var(--accent, #ec2263);
}

.pill-badge {
  padding: 2px 8px;
  border-radius: 12px;
  background: #1f2d3d;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
}
</style>
